// One Cloudflare matchmaking ticket (docs/ARCHITECTURE.md §15): a WebSocket to the edge's matchmaker shard, which
// pushes the ticket's updates, so a match arrives the moment a partner does (no polling). The probe page and the war
// both search through this.

import { NET_API, WireFormatError, parseJson, parseTicketUpdate, type MatchAssignment, type SearchPhase, type TicketUpdate } from '@taxwar/online'
import { MatchmakingError, MatchmakingFailure, type EdgeOrigin } from './backend'

const FAILURES: Readonly<Record<Extract<TicketUpdate, { kind: 'failed' }>['reason'], MatchmakingFailure>> = {
  timeout: MatchmakingFailure.timeout,
  busy: MatchmakingFailure.busy,
  room_unavailable: MatchmakingFailure.roomUnavailable,
  invite_taken: MatchmakingFailure.inviteTaken,
  bad_request: MatchmakingFailure.protocol,
}

const CLOSE_NORMAL = 1000

/** Opens a ticket with `params` (queue, and a war's country and invite) and resolves with the room once matched. */
export function openCloudflareTicket(
  edge: EdgeOrigin,
  params: URLSearchParams,
  onPhase: (phase: SearchPhase) => void,
  signal: AbortSignal,
): Promise<MatchAssignment> {
  signal.throwIfAborted()
  return new Promise((resolve, reject) => {
    const socket = new WebSocket(`${edge.ws}${NET_API.cloudflareMatchmake}?${params.toString()}`)
    let settled = false
    const settle = (outcome: () => void): void => {
      if (settled) return
      settled = true
      signal.removeEventListener('abort', abort)
      outcome()
    }
    const abort = (): void => {
      socket.close(CLOSE_NORMAL, 'search canceled')
      settle(() => reject(signal.reason))
    }
    signal.addEventListener('abort', abort, { once: true })
    socket.addEventListener('message', (event: MessageEvent<unknown>) => {
      let update: TicketUpdate
      try {
        update = parseTicketUpdate(parseJson(String(event.data), 'ticket update'))
      } catch (error) {
        if (!(error instanceof WireFormatError)) throw error
        socket.close(CLOSE_NORMAL, 'unreadable ticket update')
        settle(() => reject(new MatchmakingError(MatchmakingFailure.protocol, error.message)))
        return
      }
      if (update.kind === 'queued') onPhase({ kind: 'queued' })
      else if (update.kind === 'matched') settle(() => resolve({ matchId: update.room, roomUrl: `${edge.ws}${update.roomPath}` }))
      else settle(() => reject(new MatchmakingError(FAILURES[update.reason], `matchmaker: ${update.reason}`)))
    })
    socket.addEventListener('close', (event) =>
      settle(() => reject(new MatchmakingError(MatchmakingFailure.connectionLost, `matchmaker closed (${event.code} ${event.reason})`))),
    )
  })
}
